import React, { useState } from 'react';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaFacebook, FaInstagram, FaTwitter, FaUser, FaComment } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tour: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', tour: '', message: '' });
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  return (
    <div className="bg-white">
      {/* HERO SECTION */}
      <section 
        className="relative bg-cover bg-center text-white py-32 md:py-40"
        style={{
          backgroundImage: `linear-gradient(rgba(14, 124, 124, 0.85), rgba(11, 94, 94, 0.9)), url('/images/contact-ethiopia.jpg')`,
          marginTop: '-80px',
          paddingTop: '120px'
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-white/90">
            Ready to plan your Ethiopian journey? Our team is here to help.
          </p>
        </div>
      </section>
      
      {/* CONTACT CONTENT */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
            
            {/* LEFT COLUMN - CONTACT INFO */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our team in Addis Ababa is ready to help you design your perfect Ethiopian adventure. 
                We typically respond within 4 hours during business days.
              </p>
              
              <div className="space-y-6">
                {/* PHONE */}
                <div className="flex items-start gap-4">
                  <div className="bg-teal-50 w-14 h-14 rounded-xl flex items-center justify-center text-[#0e7c7c] text-xl flex-shrink-0">
                    <FaPhone />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <p className="text-gray-600">+251 912 345 678 (Addis Office)</p>
                    <p className="text-gray-500 text-sm">Mon–Sun: 9AM–6PM (EAT)</p>
                  </div>
                </div>
                
                {/* EMAIL */}
                <div className="flex items-start gap-4">
                  <div className="bg-teal-50 w-14 h-14 rounded-xl flex items-center justify-center text-[#0e7c7c] text-xl flex-shrink-0">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600">
                      <a href="mailto:info@encouragewomen.com" className="hover:text-[#0e7c7c] transition-colors">
                        info@encouragewomen.com
                      </a>
                    </p>
                    <p className="text-gray-600">
                      <a href="mailto:bookings@encouragewomen.com" className="hover:text-[#0e7c7c] transition-colors">
                        bookings@encouragewomen.com
                      </a>
                    </p>
                  </div>
                </div>
                
                {/* ADDRESS */}
                <div className="flex items-start gap-4">
                  <div className="bg-teal-50 w-14 h-14 rounded-xl flex items-center justify-center text-[#0e7c7c] text-xl flex-shrink-0">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                    <p className="text-gray-600">
                      Bole Road, Edna Mall Building, 4th Floor<br />
                      Addis Ababa, Ethiopia
                    </p>
                  </div>
                </div>
                
                {/* WHATSAPP */}
                <div className="flex items-start gap-4">
                  <div className="bg-teal-50 w-14 h-14 rounded-xl flex items-center justify-center text-[#0e7c7c] text-xl flex-shrink-0">
                    <FaWhatsapp />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">WhatsApp</h4>
                    <p className="text-gray-600">+251 987 654 321 (24/7 Support)</p>
                    <a 
                      href="https://wa.me/251987654321" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 bg-[#25D366] hover:bg-[#20BA5C] text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                    >
                      <FaWhatsapp className="inline mr-2" /> Chat on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
              
              {/* SOCIAL MEDIA */}
              <div className="mt-10">
                <h4 className="font-semibold text-gray-900 mb-4">Follow Us</h4>
                <div className="flex gap-3">
                  <a 
                    href="https://facebook.com/encouragewomenethiopia" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-teal-50 hover:bg-[#0e7c7c] w-12 h-12 rounded-full flex items-center justify-center text-[#0e7c7c] hover:text-white transition-all duration-300"
                  >
                    <FaFacebook size={20} />
                  </a>
                  <a 
                    href="https://instagram.com/encouragewomenethiopia" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-teal-50 hover:bg-[#0e7c7c] w-12 h-12 rounded-full flex items-center justify-center text-[#0e7c7c] hover:text-white transition-all duration-300"
                  >
                    <FaInstagram size={20} />
                  </a>
                  <a 
                    href="https://twitter.com/encouragewomen" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-teal-50 hover:bg-[#0e7c7c] w-12 h-12 rounded-full flex items-center justify-center text-[#0e7c7c] hover:text-white transition-all duration-300"
                  >
                    <FaTwitter size={20} />
                  </a>
                </div>
              </div>
            </div>
            
            {/* RIGHT COLUMN - CONTACT FORM */}
            <div>
              <div className="bg-gray-50 p-8 md:p-10 rounded-2xl shadow-lg">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Send Us a Message</h3>
                <p className="text-gray-600 mb-8">We'll respond within 4 hours</p>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUser className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Name"
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e7c7c] focus:border-transparent bg-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaEnvelope className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Email Address"
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e7c7c] focus:border-transparent bg-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <input
                      type="text"
                      name="tour"
                      value={formData.tour}
                      onChange={handleChange}
                      placeholder="Tour of Interest (optional)"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e7c7c] focus:border-transparent bg-white"
                    />
                  </div>
                  
                  <div>
                    <div className="relative">
                      <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                        <FaComment className="text-gray-400" />
                      </div>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        placeholder="Tell us about your travel dreams..."
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e7c7c] focus:border-transparent bg-white resize-vertical"
                      />
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-[#0e7c7c] hover:bg-[#0b5e5e] text-white font-bold py-4 px-6 rounded-lg transition-all duration-200 text-lg shadow-lg hover:shadow-xl ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                  
                  {submitStatus === 'success' && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-center">
                      Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* MAP SECTION (Optional) */}
      <section className="pb-20">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="bg-gray-200 h-96 rounded-2xl overflow-hidden">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.797911566198!2d38.763889!3d9.009722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b85f7c5b3b3b3%3A0x4a3a3a3a3a3a3a3a!2sEdna%20Mall!5e0!3m2!1sen!2set!4v1620000000000!5m2!1sen!2set"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Encourage Women Ethiopia Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;